import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { PostalDeliveryTypeEnum } from "@/Apollo/schema";
import { IconNavigationHome } from "@/assets/icons/figma/IconNavigationHome";
import { IconThemeShop } from "@/assets/icons/figma/IconThemeShop";
import { ProductDeliveryOptionItemProps } from "@/features/product-delivery-options/components/ProductDeliveryOptionItem";
import { ProductDeliveryOptionItemRow } from "@/features/product-delivery-options/components/ProductDeliveryOptionItemRow";
import { getProductDeliveryPriceLabel } from "@/features/product-delivery-options/components/utils/product-delivery-options.utils";
import { useProductDeliveryEstimateLabel } from "@/features/product-delivery-options/hooks/useProductDeliveryEstimateLabel";
import { showModal } from "@/ui/Modal2";

export const ProductDeliveryOptionItemPostalSendcloud = ({
  option,
  loading,
}: ProductDeliveryOptionItemProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const estimate = useProductDeliveryEstimateLabel(option);
  const { label, isFree } = getProductDeliveryPriceLabel(option, t, locale);

  const isServicePoint =
    option.deliveryType === PostalDeliveryTypeEnum.ServicePoint;

  // The service-point key is self-contained ("Within [%1$s] days to the nearest
  // location"), so it takes the raw max-days number — not the estimate label.
  const maxDays = option.deliveryEstimate?.maxDays;
  const subtitle =
    isServicePoint && maxDays
      ? t("pdp:pdp-usp-shipping-line2-service-point", { 0: maxDays })
      : estimate;

  const title = isServicePoint
    ? t("shipping-options:product-shipping-option-sendcloud-pickup-point-title")
    : t("shipping-options:product-shipping-option-sendcloud-home-title");

  const text = isServicePoint
    ? t("shipping-options:product-shipping-option-sendcloud-pickup-point-body")
    : t("shipping-options:product-shipping-option-sendcloud-home-body");

  const handleInfoClick = () => {
    showModal("pdp-details-modal", {
      title,
      text,
    });
  };

  return (
    <ProductDeliveryOptionItemRow
      method="postal-sendcloud"
      icon={
        isServicePoint ? (
          <IconThemeShop className="size-6 shrink-0 text-brand-500" />
        ) : (
          <IconNavigationHome className="size-6 shrink-0 text-brand-500" />
        )
      }
      // ponytail: placeholder keys for new Sendcloud copy
      title={
        isServicePoint
          ? t("pdp:pdp-usp-shipping-title-postal-sendcloud-service-point")
          : t("pdp:pdp-usp-shipping-title-postal-sendcloud-home")
      }
      subtitle={subtitle}
      priceLabel={label}
      isFree={isFree}
      loading={loading}
      onInfoClick={handleInfoClick}
    />
  );
};
